import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Users, Palette, Star, Calendar, Image, Paintbrush, Circle, Gem } from 'lucide-react';
import { useFrontoffWorkingMode, useSystemStatus } from '../hooks/useSystemStatus';

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { features: modeFeatures } = useFrontoffWorkingMode();
  const { tattooBookingEnabled } = useSystemStatus();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
  }, [location.hash]);


  const features = [
    {
      icon: Users,
      title: t('home.about.experience.title'),
      description: t('home.about.experience.description'),
    },
    {
      icon: Shield,
      title: t('home.about.safety.title'),
      description: t('home.about.safety.description'),
    },
    {
      icon: Palette,
      title: t('home.about.custom.title'),
      description: t('home.about.custom.description'),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="home" className="relative min-h-[60vh] py-5 flex items-center justify-center gradient-bg tattoo-pattern">
        <div className="absolute inset-0 z-0">
          {/* <img
            src="/images/image-homepage.png"
            alt="Digital Tattoo Studio"
            className="w-full h-full object-cover"
          /> */}
        </div>
        <div className="absolute inset-0 fine-grid-pattern z-10 pointer-events-none"></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-textPrimary mb-6 leading-tight">
              {t('home.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-textSecondary mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => navigate('/book-piercing?fresh=1')}
                className="btn-secondary flex items-center space-x-2 group"
              >
                <Image className="w-5 h-5" />
                <span>{t('home.hero.cta_book_piercing')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              {tattooBookingEnabled && (
                <button 
                  onClick={() => navigate('/book-tattoo?fresh=1')}
                  className="btn-secondary flex items-center space-x-2 group"
                >
                  <Palette className="w-5 h-5" />
                  <span>{t('home.hero.cta_book_tattoo')}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
        
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-textPrimary mb-4">
              {t('home.services.title')}
            </h2>
            <p className="text-lg text-textSecondary max-w-2xl mx-auto">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Tatouages Personnalisés */}
            <div className="group bg-surface-container-lowest rounded-xl overflow-hidden hover:shadow-xl transition-all duration-500 reveal">
              <div className="h-[250px] overflow-hidden">
                <img
                  src="/images/services-tattoo.jpg"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-fixed text-primary mb-6">
                  <Paintbrush className="w-6 h-6" />
                </div>
                <h3 className="font-headline-md text-headline-md mb-4">Tatouages Personnalisés</h3>
                <p className="text-on-surface-variant font-body-md mb-6">
                  Créations originales réalisées par nos artistes expérimentés, des petites pièces aux fresques complètes.
                </p>
                {modeFeatures.homepageExtraCtas && <a href="#" className="text-primary font-label-md flex items-center gap-2 group-hover:gap-4 transition-all">
                  En savoir plus <ArrowRight className="w-4 h-4" />
                </a>}
              </div>
            </div>

            {/* Piercing */}
            <div className="group bg-surface-container-lowest rounded-xl overflow-hidden hover:shadow-xl transition-all duration-500 reveal" style={{ transitionDelay: '100ms' }}>
              <div className="h-[250px] overflow-hidden">
                <img
                  src="/images/services-piercing.png"
                  alt="Piercing"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-fixed text-primary mb-6">
                  <Circle className="w-6 h-6" />
                </div>
                <h3 className="font-headline-md text-headline-md mb-4">Piercing Corporel</h3>
                <p className="text-on-surface-variant font-body-md mb-6">
                  Services de piercing professionnel avec des bijoux de haute qualité et des méthodes stériles sécurisées.
                </p>
                {modeFeatures.homepageExtraCtas && <a href="#" className="text-primary font-label-md flex items-center gap-2 group-hover:gap-4 transition-all">
                  En savoir plus <ArrowRight className="w-4 h-4" />
                </a>}
              </div>
            </div>

            {/* Vente de bijou */}
            <div className="group bg-surface-container-lowest rounded-xl overflow-hidden hover:shadow-xl transition-all duration-500 reveal" style={{ transitionDelay: '200ms' }}>
              <div className="h-[250px] overflow-hidden">
                <img
                  src="/images/abraxas-vente-bijou.png"
                  alt="Vente de bijou"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-fixed text-primary mb-6">
                  <Gem className="w-6 h-6" />
                </div>
                <h3 className="font-headline-md text-headline-md mb-4">Vente de bijou</h3>
                <p className="text-on-surface-variant font-body-md mb-6">
                  Une sélection raffinée de bijoux de piercing et d'ornements corporels de qualité, disponibles en boutique pour sublimer votre style.
                </p>
                {modeFeatures.homepageExtraCtas && <a href="#" className="text-primary font-label-md flex items-center gap-2 group-hover:gap-4 transition-all">
                  En savoir plus <ArrowRight className="w-4 h-4" />
                </a>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
    <section id="about" className="py-section-gap max-w-[1440px] mx-auto px-margin-desktop overflow-hidden py-10">
       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter items-center">
    <div className="reveal active">
    <h2 className="text-3xl sm:text-4xl font-display font-bold text-textPrimary mb-4">L'Art de la Précision depuis 1998</h2>
    <p className="text-body-lg text-on-surface-variant mb-8 leading-relaxed">Initiés par les grands maîtres, nos artistes créent des œuvres d'une finesse absolue, en parfaite harmonie avec votre peau, garantissant une expérience sereine et indolore pour une clientèle éclectique en quête d'excellence.</p>
    </div>
    <div className="reveal rounded-xl overflow-hidden shadow-2xl active" style={{ transitionDelay: '150ms' }}>
    <img alt="Abraxas" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" 
    src="/images/about-abraxas.jpg" />
    </div>
    </div>
    </div>
    </section>

      {/* Gallery Preview Section */}
      <section id="gallery" className="py-20 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-textPrimary mb-4">
              Notre travail
            </h2>
            <p className="text-lg text-textSecondary max-w-2xl mx-auto">
              Une sélection de nos travaux les plus récents et créatifs.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              '5962312C-3236-4911-B9E7-10CCF2DF9DBE-scaled.gif',
              'Abraxas-Tatoueurs-Bin-62.jpg',
              'Abraxas-Tatoueurs-Bin-73.jpg',
              'Abraxas-Tatoueurs-Chicken-04.jpg',
              'Abraxas-Tatoueurs-Chicken-05.jpg',
              'Abraxas-Tatoueurs-Manish-01.jpg',
              'Abraxas-Tatoueurs-Manish-04.jpg',
              'Abraxas-Tatoueurs-Solomon-02.jpg',
              'IMG-2260-scaled.gif',
              'IMG_0077-scaled.jpeg',
            ].map((image, index) => (
              <div
                key={index}
                className="aspect-square overflow-hidden rounded-lg group cursor-pointer"
              >
                <img
                  src={`/images/galerie/${image}`}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {modeFeatures.homepageExtraCtas && <section className="py-20 bg-gradient-to-r from-primary-300 via-accent-500 to-secondary-400 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            {t('home.cta.title')}
          </h2>
          <p className="text-lg mb-8 text-white/90">
            {t('home.cta.subtitle')}
          </p>
          <button 
            onClick={() => navigate('/quick-client-create')}
            className="bg-white text-textPrimary hover:bg-gray-100 font-medium py-4 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center space-x-2 mx-auto"
          >
            <Calendar className="w-5 h-5" />
            <span>{t('home.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>}
    </div>
  );
}